import axios from 'axios';
import { translateMessage } from '../utils/translate';

// 开发环境本地后端；生产环境 API 走专属子域名
// 也支持通过 REACT_APP_API_URL 在打包时覆盖
const isDev = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
const API_BASE_URL = process.env.REACT_APP_API_URL || (isDev ? 'http://localhost:5000/api' : 'https://api.kaufmanstrategic.vip/api');
// 只去掉末尾的 /api，避免错误删掉域名中的 'api.' 子域
const SERVER_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

/** 服务端行情推送 WebSocket */
export function getQuoteStreamUrl() {
  try {
    const u = new URL(SERVER_BASE_URL);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    u.pathname = '/ws/quotes';
    u.hash = '';
    u.search = '';
    return u.toString();
  } catch {
    return 'ws://localhost:5000/ws/quotes';
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 统一处理图片URL（将相对路径转换为完整URL）
export const getImageUrl = (url) => {
  if (!url) return null;
  const trimmedUrl = typeof url === 'string' ? url.trim() : String(url).trim();
  if (!trimmedUrl) return null;
  
  // 如果已经是完整URL，直接返回
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  
  // 处理相对路径
  if (trimmedUrl.startsWith('/')) {
    return `${SERVER_BASE_URL}${trimmedUrl}`;
  }
  
  // 添加斜杠前缀
  return `${SERVER_BASE_URL}/${trimmedUrl}`;
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - 自动翻译消息为中文
api.interceptors.response.use(
  (response) => {
    // 翻译响应中的 message 字段
    if (response.data && response.data.message) {
      response.data.message = translateMessage(response.data.message);
    }
    return response;
  },
  (error) => {
    // 翻译错误响应中的 message 字段
    if (error.response?.data?.message) {
      error.response.data.message = translateMessage(error.response.data.message);
    }
    
    // Don't redirect on login 401 errors - let the form handle it
    const isAuthRequest = error.config?.url?.includes('/auth/login');
    
    if (error.response?.status === 401 && !isAuthRequest) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API methods
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  toggleStatus: (id) => api.put(`/users/${id}/toggle-status`),
  delete: (id) => api.delete(`/users/${id}`),
  getStats: () => api.get('/users/stats'),
};

export const videosAPI = {
  getAll: (params) => api.get('/videos', { params }),
  getById: (id) => api.get(`/videos/${id}`),
  create: (data) => api.post('/videos', data),
  update: (id, data) => api.put(`/videos/${id}`, data),
  delete: (id) => api.delete(`/videos/${id}`),
};

export const documentsAPI = {
  getAll: (params) => api.get('/documents', { params }),
  getById: (id) => api.get(`/documents/${id}`),
  create: (data) => api.post('/documents', data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
};

export const tradesAPI = {
  getAll: (params) => api.get('/trades', { params }),
  getById: (id) => api.get(`/trades/${id}`),
  create: (data) => api.post('/trades', data),
  update: (id, data) => api.put(`/trades/${id}`, data),
  delete: (id) => api.delete(`/trades/${id}`),
  getStats: () => api.get('/trades/stats'),
  /** 批量实时行情（与前台同源，服务端澎博内存+短缓存） */
  getStockPrices: (symbols) => api.post('/trades/stock-prices', { symbols }),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/trades/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const strategiesAPI = {
  getAll: (params) => api.get('/strategies', { params }),
  getById: (id) => api.get(`/strategies/${id}`),
  create: (data) => api.post('/strategies', data),
  update: (id, data) => api.put(`/strategies/${id}`, data),
  delete: (id) => api.delete(`/strategies/${id}`),
};

export const aiAPI = {
  getStats: () => api.get('/ai/stats'),
  getHistory: (params) => api.get('/ai/history', { params }),
};

export const vipAPI = {
  getTiers: () => api.get('/vip/tiers'),
  getStats: () => api.get('/vip/stats'),
  upgrade: (data) => api.post('/vip/upgrade', data),
};

export const activationCodesAPI = {
  getAll: (params) => api.get('/activation-codes', { params }),
  getStats: () => api.get('/activation-codes/stats'),
  create: (data) => api.post('/activation-codes', data),
  createBatch: (data) => api.post('/activation-codes/batch', data),
  update: (id, data) => api.put(`/activation-codes/${id}`, data),
  delete: (id) => api.delete(`/activation-codes/${id}`),
};

export const tradersAPI = {
  getAll: (params) => api.get('/traders/admin', { params }),
  getById: (id) => api.get(`/traders/${id}`),
  create: (data) => api.post('/traders', data),
  update: (id, data) => api.put(`/traders/${id}`, data),
  delete: (id) => api.delete(`/traders/${id}`),
  getStats: () => api.get('/traders/stats/overview'),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post('/traders/upload-avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getAnalytics: (params) => api.get('/admin/analytics', { params }),
  updateSettings: (data) => api.post('/admin/settings', data),
  broadcast: (data) => api.post('/admin/broadcast', data),
};

export default api;

